import React, { useState } from 'react';
import { CheckCircle, Clock, XCircle, User, Calendar, MessageSquare, AlertCircle } from 'lucide-react';

const ApprovalWorkflow = ({ 
  approvalStatus = 'Pending', 
  approvalLevel = 'Department',
  approvers = [],
  approvalHistory = [],
  onApprovalChange 
}) => {
  const [showAddApprover, setShowAddApprover] = useState(false);
  const [newApprover, setNewApprover] = useState({
    name: '',
    email: '',
    role: '',
    level: 'Department',
    isRequired: true
  });

  const approvalLevels = [
    { value: 'Department', label: 'Department Level', color: '#3b82f6' },
    { value: 'Manager', label: 'Manager Level', color: '#8b5cf6' },
    { value: 'Director', label: 'Director Level', color: '#f59e0b' },
    { value: 'VP', label: 'VP Level', color: '#ef4444' },
    { value: 'C-Level', label: 'C-Level', color: '#dc2626' },
    { value: 'Board', label: 'Board Level', color: '#7c2d12' }
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Approved':
        return <CheckCircle size={16} style={{ color: '#10b981' }} />;
      case 'Rejected':
        return <XCircle size={16} style={{ color: '#ef4444' }} />;
      case 'Pending':
        return <Clock size={16} style={{ color: '#f59e0b' }} />;
      default:
        return <AlertCircle size={16} style={{ color: '#6b7280' }} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Approved':
        return '#10b981';
      case 'Rejected':
        return '#ef4444';
      case 'Pending':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  const handleAddApprover = () => {
    if (newApprover.name && newApprover.email) {
      const approver = {
        ...newApprover,
        id: Date.now().toString(),
        status: 'Pending',
        assignedAt: new Date().toISOString()
      };
      onApprovalChange('approvers', [...approvers, approver]);
      setNewApprover({
        name: '',
        email: '',
        role: '',
        level: 'Department',
        isRequired: true
      });
      setShowAddApprover(false);
    }
  };

  const handleApprovalAction = (approverId, action) => {
    const updatedApprovers = approvers.map(approver =>
      approver.id === approverId
        ? { ...approver, status: action, actionDate: new Date().toISOString() }
        : approver
    );
    
    const historyEntry = {
      id: Date.now().toString(),
      approverId,
      action,
      timestamp: new Date().toISOString(),
      comments: ''
    };

    onApprovalChange('approvers', updatedApprovers);
    onApprovalChange('approvalHistory', [...approvalHistory, historyEntry]);
  };

  const getOverallStatus = () => {
    if (approvers.length === 0) return 'Not Started';
    
    const hasRejected = approvers.some(a => a.status === 'Rejected');
    if (hasRejected) return 'Rejected';
    
    const allApproved = approvers.every(a => a.status === 'Approved');
    if (allApproved) return 'Approved';
    
    const hasPending = approvers.some(a => a.status === 'Pending');
    if (hasPending) return 'Pending';
    
    return 'In Progress';
  };

  const overallStatus = getOverallStatus();

  return (
    <div style={{ width: '100%' }}>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        marginBottom: '16px' 
      }}>
        <h6 style={{ 
          margin: 0, 
          color: '#374151', 
          fontWeight: '600',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
        }}>
          Approval Workflow
        </h6>
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: '8px',
          padding: '6px 12px',
          backgroundColor: getStatusColor(overallStatus) + '20',
          color: getStatusColor(overallStatus),
          borderRadius: '6px',
          fontSize: '12px',
          fontWeight: '500'
        }}>
          {getStatusIcon(overallStatus)}
          {overallStatus}
        </div>
      </div>

      {/* Approval Level Selection */}
      <div style={{ marginBottom: '16px' }}>
        <label style={{ 
          display: 'block', 
          fontSize: '12px', 
          fontWeight: '500', 
          color: '#374151', 
          marginBottom: '6px' 
        }}>
          Approval Level
        </label>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {approvalLevels.map((level) => (
            <button
              key={level.value}
              type="button"
              onClick={() => onApprovalChange('approvalLevel', level.value)}
              style={{
                padding: '6px 12px',
                backgroundColor: approvalLevel === level.value ? level.color : '#f3f4f6',
                color: approvalLevel === level.value ? 'white' : '#374151',
                border: '1px solid',
                borderColor: approvalLevel === level.value ? level.color : '#d1d5db',
                borderRadius: '6px',
                fontSize: '11px',
                fontWeight: '500',
                cursor: 'pointer',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
              }}
            >
              {level.label}
            </button>
          ))}
        </div>
      </div>

      {/* Add Approver Form */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          marginBottom: '8px' 
        }}>
          <label style={{ 
            fontSize: '12px', 
            fontWeight: '500', 
            color: '#374151' 
          }}>
            Approvers ({approvers.length})
          </label>
          <button
            type="button"
            onClick={() => setShowAddApprover(!showAddApprover)}
            style={{
              padding: '4px 8px',
              backgroundColor: '#007B8F',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              fontSize: '11px',
              cursor: 'pointer',
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
            }}
          >
            {showAddApprover ? 'Cancel' : 'Add Approver'}
          </button>
        </div>

        {showAddApprover && (
          <div style={{
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px',
            backgroundColor: '#f9fafb',
            marginBottom: '12px'
          }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                placeholder="Approver Name"
                value={newApprover.name}
                onChange={(e) => setNewApprover({ ...newApprover, name: e.target.value })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
              <input
                type="email"
                placeholder="Email"
                value={newApprover.email}
                onChange={(e) => setNewApprover({ ...newApprover, email: e.target.value })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
            </div>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
              <input
                type="text"
                placeholder="Role/Title"
                value={newApprover.role}
                onChange={(e) => setNewApprover({ ...newApprover, role: e.target.value })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              />
              <select
                value={newApprover.level}
                onChange={(e) => setNewApprover({ ...newApprover, level: e.target.value })}
                style={{
                  flex: 1,
                  padding: '6px 8px',
                  border: '1px solid #d1d5db',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                {approvalLevels.map(level => (
                  <option key={level.value} value={level.value}>{level.label}</option>
                ))}
              </select>
            </div>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center' 
            }}>
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '4px', 
                fontSize: '11px', 
                color: '#374151',
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={newApprover.isRequired}
                  onChange={(e) => setNewApprover({ ...newApprover, isRequired: e.target.checked })}
                  style={{ margin: 0 }}
                />
                Required Approver
              </label>
              <button
                type="button"
                onClick={handleAddApprover}
                style={{
                  padding: '4px 8px',
                  backgroundColor: '#007B8F',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '11px',
                  cursor: 'pointer',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}
              >
                Add Approver
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Approvers List */}
      <div style={{ 
        border: '1px solid #e5e7eb', 
        borderRadius: '8px', 
        backgroundColor: 'white',
        marginBottom: '16px'
      }}>
        {approvers.length === 0 ? (
          <div style={{ 
            padding: '16px', 
            textAlign: 'center', 
            color: '#6b7280',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
          }}>
            <User size={24} style={{ marginBottom: '8px', opacity: 0.5 }} />
            <div style={{ fontSize: '12px' }}>No approvers assigned</div>
          </div>
        ) : (
          approvers.map((approver, index) => (
            <div
              key={approver.id}
              style={{
                padding: '12px 16px',
                borderBottom: index < approvers.length - 1 ? '1px solid #f3f4f6' : 'none',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <div style={{ flex: 1 }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px', 
                  marginBottom: '4px' 
                }}>
                  <h6 style={{ 
                    margin: 0, 
                    fontSize: '12px', 
                    fontWeight: '600', 
                    color: '#374151',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}>
                    {approver.name}
                  </h6>
                  <span style={{
                    padding: '2px 6px',
                    backgroundColor: approvalLevels.find(l => l.value === approver.level)?.color + '20',
                    color: approvalLevels.find(l => l.value === approver.level)?.color,
                    borderRadius: '4px',
                    fontSize: '10px',
                    fontWeight: '500'
                  }}>
                    {approver.level}
                  </span>
                  {approver.isRequired && (
                    <span style={{
                      padding: '2px 6px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '10px',
                      fontWeight: '500'
                    }}>
                      Required
                    </span>
                  )}
                </div>
                <div style={{ 
                  fontSize: '11px', 
                  color: '#6b7280',
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                }}>
                  {approver.email} • {approver.role}
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '4px',
                  padding: '4px 8px',
                  backgroundColor: getStatusColor(approver.status) + '20',
                  color: getStatusColor(approver.status),
                  borderRadius: '4px',
                  fontSize: '10px',
                  fontWeight: '500'
                }}>
                  {getStatusIcon(approver.status)}
                  {approver.status}
                </div>
                {approver.status === 'Pending' && (
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <button
                      type="button"
                      onClick={() => handleApprovalAction(approver.id, 'Approved')}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                      }}
                    >
                      Approve
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApprovalAction(approver.id, 'Rejected')}
                      style={{
                        padding: '4px 8px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        fontSize: '10px',
                        cursor: 'pointer',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                      }}
                    >
                      Reject
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Approval History */}
      {approvalHistory.length > 0 && (
        <div>
          <h6 style={{ 
            margin: '0 0 8px 0', 
            fontSize: '12px', 
            fontWeight: '600', 
            color: '#374151',
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
          }}>
            Approval History
          </h6>
          <div style={{ 
            border: '1px solid #e5e7eb', 
            borderRadius: '8px', 
            backgroundColor: 'white',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            {approvalHistory.map((entry, index) => (
              <div
                key={entry.id}
                style={{
                  padding: '8px 12px',
                  borderBottom: index < approvalHistory.length - 1 ? '1px solid #f3f4f6' : 'none',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {getStatusIcon(entry.action)}
                <div style={{ flex: 1 }}>
                  <div style={{ 
                    fontSize: '11px', 
                    fontWeight: '500', 
                    color: '#374151',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}>
                    {entry.action} by {approvers.find(a => a.id === entry.approverId)?.name || 'Unknown'}
                  </div>
                  <div style={{ 
                    fontSize: '10px', 
                    color: '#6b7280',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}>
                    {new Date(entry.timestamp).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ApprovalWorkflow;
